import { Box, IconButton, Typography } from '@mui/material'
import screenfull from 'screenfull'
import Close from '@mui/icons-material/Close'
import Fullscreen from '@mui/icons-material/Fullscreen'
import { addMinutes, format, isValid, parseISO } from 'date-fns'

import { useActiveVideoVisit } from '~/shared/hooks/activeVideoVisit'
import { useZoomSession } from './SessionProvider'
import logo from './zoom.png'
import { useCallback, useEffect, useState } from 'react'
import AppointmentVisitFeedback from '../AppointmentVisitFeedback'
import { logger } from '~/shared/lib/logger'
import { fireAndForget } from '~/shared/lib/fireAndForget'

/**
 * Minutes in a Django DurationField, which the API serialises as `HH:MM:SS`.
 *
 * `duration` is a bare `string` in the contract, so anything can arrive here.
 * Unreadable input counts as no duration rather than propagating a NaN into the
 * date arithmetic, where `format` would throw on the resulting invalid date.
 */
const durationInMinutes = (value: string | null | undefined) => {
  if (!value) return 0
  const [hours = 0, minutes = 0, seconds = 0] = value.split(':').map(Number)
  const total = hours * 60 + minutes + seconds / 60
  return Number.isFinite(total) ? total : 0
}

export const SessionHeader: React.FC = () => {
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const [feedbackModalDisplayed, setFeedbackModalDisplayed] = useState(false)
  const { clearActiveVideoVisit } = useActiveVideoVisit()

  const { containerRef, appointment, modal, setScreen } = useZoomSession()

  const listener = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!feedbackModalDisplayed) setShowFeedbackModal(true)
      if (!showFeedbackModal) setFeedbackModalDisplayed(true)
      event.preventDefault()
    },
    [setShowFeedbackModal, showFeedbackModal, feedbackModalDisplayed, setFeedbackModalDisplayed],
  )

  useEffect(() => {
    window.addEventListener('beforeunload', listener)

    // Remove listener when component is unmounted.
    return () => {
      window.removeEventListener('beforeunload', listener)
    }
  }, [listener])

  // Appointment description and timing. Without a usable start there is no time
  // range to show, so the title falls back to the description alone.
  const startsAt = appointment?.start ? parseISO(appointment.start) : null
  const timeRange =
    startsAt && isValid(startsAt)
      ? `${format(startsAt, 'h:mm')} - ${format(addMinutes(startsAt, durationInMinutes(appointment?.duration)), 'h:mma')}`
      : ''
  const title = [timeRange, appointment?.description].filter(Boolean).join(' | ')
  return (
    <Box
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: theme.palette.common.white,
      })}
      id="VIDEO_VISIT_MODAL"
    >
      <Box sx={{ overflow: 'hidden', whiteSpace: 'nowrap', px: 2 }}>
        <Typography
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: '1.5rem !important',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box component="img" src={logo} sx={{ height: '1em' }} alt="Zoom" />
        <IconButton
          onClick={() => {
            if (screenfull.isEnabled)
              fireAndForget('[Zoom] toggle fullscreen', () =>
                screenfull.toggle(containerRef.current ?? undefined),
              )
          }}
        >
          <Fullscreen />
        </IconButton>
        {modal ? (
          <IconButton
            onClick={() => {
              setScreen('precall')
              setShowFeedbackModal(true)
            }}
          >
            <Close />
          </IconButton>
        ) : null}
        {showFeedbackModal ? (
          <AppointmentVisitFeedback
            onClose={() => {
              setShowFeedbackModal(false)
              logger.warn('Feedback modal closed. Leaving appointment.')
              clearActiveVideoVisit()
            }}
            appointment={appointment}
          />
        ) : null}
      </Box>
    </Box>
  )
}
